import { z } from "@hono/zod-openapi";
import { UNCATEGORIZED_CATEGORY } from "./base";
import { tmsEventCatalog } from "./catalog";
import type { TmsEvent, TmsEventCategory, TmsEventCategoryOf, TmsEventKind } from "./taxonomy";
import { TMS_EVENT_KINDS, tmsEventSchemaOver } from "./taxonomy";
import type { PublishedTmsEventKind } from "./tmsEventSubjects";
import { isPublishedTmsEventKind } from "./tmsEventSubjects";

/**
 * The catalog narrowed to the kinds a client can be sent: those whose subject's stream is published.
 *
 * A category every one of whose kinds is internal disappears with them, so the published vocabulary's categories are
 * exactly the ones a published kind sits under.
 */
const publishedCatalog: Parameters<typeof tmsEventSchemaOver>[0] = Object.fromEntries(
  Object.entries(tmsEventCatalog).flatMap(([category, kinds]) => {
    const published = Object.fromEntries(
      Object.entries(kinds).filter(([kind]) => isPublishedTmsEventKind(kind as TmsEventKind)),
    );

    return Object.keys(published).length === 0 ? [] : [[category, published]];
  }),
);

/**
 * Every kind a client can be sent, and the enum the spec registers under the name the full vocabulary used to hold.
 *
 * The web's display tables are keyed on the generated type and must be total, so what is registered here decides
 * exactly which kinds a client has to know about: the published ones, and no other.
 */
export const PUBLISHED_TMS_EVENT_KINDS: readonly PublishedTmsEventKind[] =
  TMS_EVENT_KINDS.filter(isPublishedTmsEventKind);

export const PublishedTmsEventKindSchema = z
  .enum(PUBLISHED_TMS_EVENT_KINDS as [PublishedTmsEventKind, ...PublishedTmsEventKind[]])
  .openapi("TmsEventKind");

/**
 * The categories a published kind sits under, minus the reserved key.
 */
export type PublishedTmsEventCategory = Exclude<
  TmsEventCategoryOf<PublishedTmsEventKind>,
  typeof UNCATEGORIZED_CATEGORY
>;

export const PUBLISHED_TMS_EVENT_CATEGORIES: readonly PublishedTmsEventCategory[] = (
  Object.keys(publishedCatalog) as (TmsEventCategory | typeof UNCATEGORIZED_CATEGORY)[]
).filter((category): category is PublishedTmsEventCategory => category !== UNCATEGORIZED_CATEGORY);

export const PublishedTmsEventCategorySchema = z
  .enum(PUBLISHED_TMS_EVENT_CATEGORIES as [PublishedTmsEventCategory, ...PublishedTmsEventCategory[]])
  .openapi("TmsEventCategory");

/**
 * One published kind's DTO, at any of its versions.
 */
export type PublishedTmsEvent = Extract<TmsEvent, { kind: PublishedTmsEventKind }>;

/**
 * The wire union: every kind of every published subject, at every version it declares, discriminated on `kind`.
 *
 * What the stream endpoint's page is declared with, and so what puts kinds into the generated spec — these and no
 * others. Built by the same derivation as `TmsEventSchema`, over the catalog with the internal subjects' kinds left
 * out, so a kind is on the wire exactly when its subject's stream is published and never by a separate decision.
 *
 * One cast, at the seam between the runtime derivation — which is over a filtered map and so untyped — and the
 * type-level narrowing of the full union, which names the same arms.
 */
export const PublishedTmsEventSchema = (
  tmsEventSchemaOver(publishedCatalog) as unknown as z.ZodType<PublishedTmsEvent>
).openapi("TmsEvent");
